import { useEffect, useId, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { safeHref, type RichDoc } from '@agency/contracts';
import { Button, TextField } from '@agency/ui';
import './content.css';

/**
 * TipTap restricted to the vocabulary the API accepts (rich.ts): h2/h3, lists, quote, code, bold/italic/code, https or site links.
 * The API validates again; this just keeps editors from producing anything that would be refused.
 */
export function RichEditor({ label, value, onChange, readOnly, error, hint }: { label: string; value: RichDoc; onChange: (doc: RichDoc) => void; readOnly?: boolean; error?: string; hint?: string }) {
  const id = useId();
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const last = useRef(JSON.stringify(value));
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, strike: false, underline: false, link: { openOnClick: false, autolink: false, HTMLAttributes: { rel: 'noopener noreferrer' } } })],
    content: value as object,
    editable: !readOnly,
    editorProps: { attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-labelledby': `${id}-l`, ...(hint ? { 'aria-describedby': `${id}-h` } : {}), class: 'rich__content' } },
    onUpdate: ({ editor: ed }) => {
      const json = ed.getJSON() as RichDoc;
      last.current = JSON.stringify(json);
      onChange(json);
    },
    immediatelyRender: false,
  });
  // External changes (reload, restore) replace the content without echoing an update back.
  useEffect(() => {
    if (!editor) return;
    const next = JSON.stringify(value);
    if (next !== last.current) { last.current = next; editor.commands.setContent(value as object, { emitUpdate: false }); }
  }, [value, editor]);
  useEffect(() => { editor?.setEditable(!readOnly); }, [readOnly, editor]);
  if (!editor) return null;

  const btn = (name: string, active: boolean, run: () => void, text: string, extra?: { disabled?: boolean }) => (
    <button type="button" className="rich__btn" aria-pressed={active} aria-label={name} title={name} disabled={readOnly || extra?.disabled} onClick={run}>{text}</button>
  );
  const applyLink = () => {
    const v = url.trim();
    if (!safeHref.safeParse(v).success || v.startsWith('#')) { setLinkError('Use an https:// link or a /path on this site'); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: v }).run();
    setLinkOpen(false); setUrl(''); setLinkError(null);
  };
  const c = editor.chain().focus();
  return (
    <div className="ag-field">
      <span id={`${id}-l`} className="ag-label">{label}</span>
      {hint ? <span id={`${id}-h`} className="ag-hint">{hint}</span> : null}
      <div className="rich" data-invalid={error ? true : undefined}>
        <div className="rich__bar" role="toolbar" aria-label={`${label} formatting`}>
          {btn('Bold', editor.isActive('bold'), () => c.toggleBold().run(), 'B')}
          {btn('Italic', editor.isActive('italic'), () => c.toggleItalic().run(), 'I')}
          {btn('Inline code', editor.isActive('code'), () => c.toggleCode().run(), '</>')}
          {btn('Heading 2', editor.isActive('heading', { level: 2 }), () => c.toggleHeading({ level: 2 }).run(), 'H2')}
          {btn('Heading 3', editor.isActive('heading', { level: 3 }), () => c.toggleHeading({ level: 3 }).run(), 'H3')}
          {btn('Bulleted list', editor.isActive('bulletList'), () => c.toggleBulletList().run(), '• List')}
          {btn('Numbered list', editor.isActive('orderedList'), () => c.toggleOrderedList().run(), '1. List')}
          {btn('Quote', editor.isActive('blockquote'), () => c.toggleBlockquote().run(), '“ ”')}
          {btn('Code block', editor.isActive('codeBlock'), () => c.toggleCodeBlock().run(), '{ }')}
          {btn(editor.isActive('link') ? 'Edit link' : 'Add link', editor.isActive('link'), () => { setUrl((editor.getAttributes('link').href as string) ?? ''); setLinkOpen(true); }, 'Link')}
          {btn('Remove link', false, () => c.unsetLink().run(), 'Unlink', { disabled: !editor.isActive('link') })}
          {btn('Undo', false, () => c.undo().run(), '↶', { disabled: !editor.can().undo() })}
          {btn('Redo', false, () => c.redo().run(), '↷', { disabled: !editor.can().redo() })}
        </div>
        {linkOpen ? (
          <div className="rich__link">
            <TextField label="Link address" value={url} onChange={(e) => setUrl(e.target.value)} error={linkError ?? undefined} data-autofocus onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setLinkOpen(false); }} />
            <Button size="sm" variant="primary" onClick={applyLink}>Apply</Button>
            <Button size="sm" onClick={() => { setLinkOpen(false); setLinkError(null); }}>Cancel</Button>
          </div>
        ) : null}
        <EditorContent editor={editor} />
      </div>
      {error ? <span className="ag-error" role="alert">{error}</span> : null}
    </div>
  );
}
